// packages/web/src/components/admin/uren-edit/entry-edit-form.tsx
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useForm } from "@tanstack/react-form";
import { z } from "zod";
import { useUpdateEntry, useTransitionEntry } from "@/api/hours-entries/mutations";
import { useUpdateDayBreak } from "@/api/day-breaks/mutations";
import { planEntrySave, type EntryAfter, type EntryBefore } from "@/lib/plan-entry-save";
import { EntryRejectModal } from "./entry-reject-modal.tsx";

export interface EntryEditFormInitial extends EntryBefore {
  entryId: string;
  status: "to_check" | "rejected" | "checked" | "exported";
  workerName: string;
  workerId: string;
  date: string;
  actorIsSuperAdmin: boolean;
  actorIsApprover: boolean;
  approverUserId: string;
}

interface EntryEditFormProps {
  initial: EntryEditFormInitial;
  workorderOptions: { id: string; number: string }[];
  craneOptions: { id: string; number: string }[];
  actorUserId: string;
}

interface FormValues {
  workorderId: string | null;
  craneId: string | null;
  startHHmm: string;
  endHHmm: string;
  dayBreakMinutes: number;
  note: string;
}

const hhmm = /^([01]\d|2[0-3]):[0-5]\d$/;

const schema = z
  .object({
    workorderId: z.string().nullable(),
    craneId: z.string().nullable(),
    startHHmm: z.string().regex(hhmm, { message: "HH:MM" }),
    endHHmm: z.string().regex(hhmm, { message: "HH:MM" }),
    dayBreakMinutes: z.number().int().min(0),
    note: z.string(),
  })
  .refine((v) => hhmmToMinutes(v.endHHmm) > hhmmToMinutes(v.startHHmm), {
    message: "Eindtijd moet na begintijd liggen",
    path: ["endHHmm"],
  });

function hhmmToMinutes(s: string): number {
  if (!hhmm.test(s)) return Number.NaN;
  const [h, m] = s.split(":").map((x) => parseInt(x, 10));
  return h! * 60 + m!;
}

function msToHHmm(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
}

function hhmmToMs(dateISO: string, s: string): number {
  const [h, m] = s.split(":").map((x) => parseInt(x, 10));
  const t = Date.parse(`${dateISO}T00:00:00.000Z`);
  return t + (h! * 60 + m!) * 60_000;
}

export function EntryEditForm({
  initial,
  workorderOptions,
  craneOptions,
  actorUserId,
}: EntryEditFormProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const update = useUpdateEntry();
  const transition = useTransitionEntry();
  const updateBreak = useUpdateDayBreak();
  const pending = update.isPending || transition.isPending || updateBreak.isPending;
  const exported = initial.status === "exported";

  const form = useForm({
    defaultValues: {
      workorderId: initial.workorderId,
      craneId: initial.craneId,
      startHHmm: msToHHmm(initial.startAt),
      endHHmm: msToHHmm(initial.endAt),
      dayBreakMinutes: initial.dayBreakMinutes,
      note: initial.note,
    } satisfies FormValues,
    validators: { onSubmit: schema },
    onSubmit: async ({ value }) => {
      const after: EntryAfter = {
        startAt: hhmmToMs(initial.date, value.startHHmm),
        endAt: hhmmToMs(initial.date, value.endHHmm),
        workorderId: value.workorderId,
        craneId: value.craneId,
        note: value.note,
        dayBreakId: initial.dayBreakId,
        dayBreakMinutes: value.dayBreakMinutes,
      };
      const steps = planEntrySave(initial, after);
      if (steps.length === 0) {
        navigate({ to: "/admin/uren" });
        return;
      }
      try {
        for (const step of steps) {
          if (step.op === "update") {
            await update.mutateAsync({ id: initial.entryId, body: step.body });
          } else if (step.op === "updateDayBreak") {
            await updateBreak.mutateAsync({
              userId: initial.workerId,
              date: initial.date,
              body: { breakDurationMinutes: step.body.minutes },
            });
          }
        }
        toast.success(t("admin.uren.toasts.saved"));
        navigate({ to: "/admin/uren" });
      } catch (err) {
        toast.error((err as Error).message);
      }
    },
  });

  useEffect(() => {
    form.reset();
  }, [initial.entryId, form]);

  function onApprove() {
    transition.mutate(
      { id: initial.entryId, body: { target: "checked" } },
      {
        onSuccess: () => {
          toast.success(t("admin.uren.toasts.saved"));
          navigate({ to: "/admin/uren" });
        },
        onError: (err) => toast.error((err as Error).message),
      },
    );
  }

  function onRevert() {
    transition.mutate(
      { id: initial.entryId, body: { target: "to_check" } },
      {
        onSuccess: () => {
          toast.success(t("admin.uren.toasts.saved"));
          navigate({ to: "/admin/uren" });
        },
        onError: (err) => toast.error((err as Error).message),
      },
    );
  }

  const canApprove =
    initial.status === "to_check" &&
    !exported &&
    (initial.actorIsSuperAdmin ||
      (initial.actorIsApprover && initial.approverUserId === actorUserId));
  const canReject = initial.status === "to_check" && !exported && canApprove;
  const canRevert = initial.status === "checked" && initial.actorIsSuperAdmin && !exported;

  return (
    <RejectAware initial={initial} canReject={canReject} navigate={navigate}>
      {({ openReject }) => (
        <div className="flex flex-col">
          <div className="border-b border-line px-6 py-3 flex items-center gap-4">
            <button
              type="button"
              onClick={() => navigate({ to: "/admin/uren" })}
              className="size-8 rounded border border-line"
              aria-label="Terug"
            >
              ←
            </button>
            <h1 className="font-sans-condensed text-[17px] font-semibold">
              {t("admin.uren.edit.title", { name: initial.workerName, date: initial.date })}
            </h1>
          </div>
          {exported && (
            <div className="border-b border-line bg-surface-1 px-6 py-2 text-xs text-ink-500">
              {t("admin.uren.edit.exported-banner")}
            </div>
          )}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <div className="flex flex-col gap-4 px-6 py-4 max-w-[640px]">
              <div className="grid grid-cols-2 gap-4">
                <form.Field name="workorderId">
                  {(field) => (
                    <label className="flex flex-col text-xs">
                      <span className="mb-1 text-ink-500">
                        {t("admin.uren.edit.field-workorder")}
                      </span>
                      <select
                        value={field.state.value ?? ""}
                        disabled={exported}
                        onChange={(e) => field.handleChange(e.target.value || null)}
                        onBlur={field.handleBlur}
                        className="rounded border border-line bg-surface p-2"
                      >
                        <option value="">—</option>
                        {workorderOptions.map((wo) => (
                          <option key={wo.id} value={wo.id}>
                            {wo.number}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                </form.Field>

                <form.Field name="craneId">
                  {(field) => (
                    <label className="flex flex-col text-xs">
                      <span className="mb-1 text-ink-500">{t("admin.uren.edit.field-crane")}</span>
                      <select
                        value={field.state.value ?? ""}
                        disabled={exported}
                        onChange={(e) => field.handleChange(e.target.value || null)}
                        onBlur={field.handleBlur}
                        className="rounded border border-line bg-surface p-2"
                      >
                        <option value="">—</option>
                        {craneOptions.map((cr) => (
                          <option key={cr.id} value={cr.id}>
                            {cr.number}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}
                </form.Field>

                <form.Field name="startHHmm">
                  {(field) => (
                    <label className="flex flex-col text-xs">
                      <span className="mb-1 text-ink-500">{t("admin.uren.edit.field-start")}</span>
                      <input
                        type="time"
                        value={field.state.value}
                        disabled={exported}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        className="rounded border border-line bg-surface p-2 tabular-nums"
                      />
                    </label>
                  )}
                </form.Field>

                <form.Field name="endHHmm">
                  {(field) => {
                    const err = field.state.meta.errors[0];
                    const msg = typeof err === "string" ? err : err?.message;
                    return (
                      <label className="flex flex-col text-xs">
                        <span className="mb-1 text-ink-500">{t("admin.uren.edit.field-end")}</span>
                        <input
                          type="time"
                          value={field.state.value}
                          disabled={exported}
                          onChange={(e) => field.handleChange(e.target.value)}
                          onBlur={field.handleBlur}
                          aria-invalid={!!msg}
                          className={`rounded border ${msg ? "border-vroom-red" : "border-line"} bg-surface p-2 tabular-nums`}
                        />
                        {msg && <span className="mt-1 text-vroom-red">{msg}</span>}
                      </label>
                    );
                  }}
                </form.Field>
              </div>

              <form.Field name="dayBreakMinutes">
                {(field) => (
                  <label className="flex flex-col text-xs">
                    <span className="mb-1 text-ink-500">{t("admin.uren.edit.field-break")}</span>
                    <input
                      type="number"
                      min={0}
                      value={field.state.value}
                      disabled={exported || !initial.dayBreakId}
                      onChange={(e) =>
                        field.handleChange(Math.max(0, parseInt(e.target.value, 10) || 0))
                      }
                      onBlur={field.handleBlur}
                      className="rounded border border-line bg-surface p-2 tabular-nums w-32"
                    />
                  </label>
                )}
              </form.Field>

              <form.Field name="note">
                {(field) => (
                  <label className="flex flex-col text-xs">
                    <span className="mb-1 text-ink-500">{t("admin.uren.edit.field-note")}</span>
                    <textarea
                      value={field.state.value}
                      disabled={exported}
                      onChange={(e) => field.handleChange(e.target.value)}
                      onBlur={field.handleBlur}
                      className="rounded border border-line bg-surface p-2 min-h-[72px]"
                    />
                  </label>
                )}
              </form.Field>

              {!exported && (
                <div className="flex items-center gap-2 pt-2 border-t border-line">
                  {canReject && (
                    <button
                      type="button"
                      onClick={openReject}
                      className="px-3 py-1.5 text-sm rounded border border-vroom-red text-vroom-red bg-vroom-red-soft"
                    >
                      {t("admin.uren.edit.reject")}
                    </button>
                  )}
                  {canApprove && (
                    <button
                      type="button"
                      onClick={onApprove}
                      disabled={pending}
                      className="px-3 py-1.5 text-sm rounded bg-st-ok text-white disabled:opacity-60"
                    >
                      {t("admin.uren.edit.approve")}
                    </button>
                  )}
                  {canRevert && (
                    <button
                      type="button"
                      onClick={onRevert}
                      disabled={pending}
                      className="px-3 py-1.5 text-sm rounded border border-line bg-surface"
                    >
                      {t("admin.uren.edit.revert")}
                    </button>
                  )}
                </div>
              )}
            </div>

            {!exported && (
              <div className="border-t border-line px-6 py-3 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => navigate({ to: "/admin/uren" })}
                  disabled={pending}
                  className="px-3 py-1.5 text-sm rounded border border-line bg-surface"
                >
                  {t("admin.uren.edit.cancel")}
                </button>
                <button
                  type="submit"
                  disabled={pending}
                  className="px-3 py-1.5 text-sm rounded bg-p-600 text-white disabled:opacity-60"
                >
                  {t("admin.uren.edit.save")}
                </button>
              </div>
            )}
          </form>
        </div>
      )}
    </RejectAware>
  );
}

// Reject-modal lives outside the <form> so its own <form> doesn't nest.
function RejectAware({
  initial,
  canReject,
  navigate,
  children,
}: {
  initial: EntryEditFormInitial;
  canReject: boolean;
  navigate: ReturnType<typeof useNavigate>;
  children: (api: { openReject: () => void }) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      {children({ openReject: () => setOpen(true) })}
      {canReject && (
        <EntryRejectModal
          open={open}
          entryId={initial.entryId}
          onClose={() => setOpen(false)}
          onRejected={() => {
            setOpen(false);
            navigate({ to: "/admin/uren" });
          }}
        />
      )}
    </>
  );
}
